import {useCallback, useMemo} from 'react';

import {useFormat} from '../../../util/format-hook/format-hook';
import {useUrl} from '../../../util/url-hook/url-hook';

import {
    getCompareModeState,
    getFilterStateFromUrlQuery,
    serializeCompetitorDrawerFilters,
    serializePeriodDrawerFilters,
    serializeSingleDrawerFilters,
} from './reviews-analysis-helper';
import {
    ComparePeriodFilterRequestType,
    CompetitorFilterRequestType,
    ReviewsAnalysisFilterType,
    ReviewsCompareEnum,
    SingleFilterRequestType,
    UseReviewsAnalysisFilterType,
} from './reviews-analysis-type';

export function useReviewsAnalysisFilter(
    isTopicsAllowed = true,
    defaultDateRange?: [Date, Date]
): UseReviewsAnalysisFilterType {
    const {queries, replaceQuery} = useUrl<ReviewsAnalysisFilterType>();
    const filter: ReviewsAnalysisFilterType = useMemo<ReviewsAnalysisFilterType>(
        (): ReviewsAnalysisFilterType => getFilterStateFromUrlQuery(queries),
        [queries]
    );
    const compareMode: ReviewsCompareEnum = useMemo<ReviewsCompareEnum>((): ReviewsCompareEnum => {
        return getCompareModeState(filter);
    }, [filter]);
    const singleFilter: SingleFilterRequestType = useMemo<SingleFilterRequestType>(
        (): SingleFilterRequestType => serializeSingleDrawerFilters(filter, isTopicsAllowed, defaultDateRange),
        [defaultDateRange, filter, isTopicsAllowed]
    );
    const comparePeriodFilter: ComparePeriodFilterRequestType = useMemo<ComparePeriodFilterRequestType>(
        (): ComparePeriodFilterRequestType => serializePeriodDrawerFilters(filter),
        [filter]
    );
    const competitorFilter: CompetitorFilterRequestType = useMemo<CompetitorFilterRequestType>(
        (): CompetitorFilterRequestType => serializeCompetitorDrawerFilters(filter, defaultDateRange),
        [defaultDateRange, filter]
    );
    const setFilter = useCallback(
        (filterPartial: Partial<ReviewsAnalysisFilterType>): void => {
            replaceQuery(filterPartial);
        },
        [replaceQuery]
    );

    return {filter, singleFilter, comparePeriodFilter, competitorFilter, setFilter, queries, compareMode};
}

export function useReviewsAnalysisTooltipFormatter(): (label: string, compareLabelFormat?: boolean) => string {
    const {comparePeriodFilter, singleFilter} = useReviewsAnalysisFilter(false);
    const {getFormattedDateTime} = useFormat();

    const isCompareMode: boolean = useMemo(() => {
        return Boolean(comparePeriodFilter.startDate);
    }, [comparePeriodFilter.startDate]);

    return useCallback(
        (label: string, compareLabelFormat?: boolean) => {
            const startDate = singleFilter.startDate;
            const endDate = singleFilter.endDate;

            const compareStartDate = comparePeriodFilter.startDate;
            const compareEndDate = comparePeriodFilter.endDate;

            if (compareLabelFormat && compareStartDate && compareEndDate) {
                return `${getFormattedDateTime(new Date(compareStartDate), {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                })} - ${getFormattedDateTime(new Date(compareEndDate), {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                })}`;
            }

            if (isCompareMode && startDate && endDate) {
                return `${getFormattedDateTime(new Date(startDate), {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                })} - ${getFormattedDateTime(new Date(endDate), {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                })}`;
            }

            return label;
        },
        [comparePeriodFilter, getFormattedDateTime, isCompareMode, singleFilter]
    );
}
